/**
 * Video Details contextual destinations — optional nav CTAs beside Play / My Formation.
 *
 * Pattern: build an ordered candidate list; callers render **all** resolvable actions
 * (Organization then Creator, then future Series / Collection / Event).
 * `resolvePrimaryVideoContextualAction` remains for single-CTA callers.
 *
 * Action shape: { key, label, to, ariaLabel? }
 */

import { organizationPublicPath } from "./organizations";
import { creatorPublicPath } from "./creators";

function trimSlug(value) {
  if (value == null) return "";
  return String(value).trim();
}

function readAssociation(video, nestedKey, flatIdKey, flatSlugKey, flatNameKey) {
  if (!video || typeof video !== "object") {
    return null;
  }

  const nested = video[nestedKey];
  if (nested && typeof nested === "object") {
    const slug = trimSlug(nested.slug);
    if (slug) {
      return {
        id: nested.id != null ? nested.id : null,
        slug,
        name: nested.name || nested.display_name || "",
        public_path: nested.public_path || "",
      };
    }
  }

  const ownership =
    video.media_asset && video.media_asset.ownership
      ? video.media_asset.ownership
      : null;
  const ownershipSlug = ownership
    ? trimSlug(ownership[flatSlugKey] || ownership[`${nestedKey}_slug`])
    : "";
  if (ownershipSlug) {
    return {
      id: ownership[flatIdKey] != null ? ownership[flatIdKey] : null,
      slug: ownershipSlug,
      name: ownership[flatNameKey] || "",
      public_path: "",
    };
  }

  const flatSlug = trimSlug(video[flatSlugKey]);
  if (flatSlug) {
    return {
      id: video[flatIdKey] != null ? video[flatIdKey] : null,
      slug: flatSlug,
      name: video[flatNameKey] || "",
      public_path: "",
    };
  }

  return null;
}

/**
 * Ordered optional destinations for a video details payload.
 * Precedence: Organization → Creator → (future: Series, Collection, Event).
 *
 * @param {object} videoDetails
 * @returns {Array<{ key: string, label: string, to: string, ariaLabel?: string }>}
 */
export function resolveVideoContextualActions(videoDetails) {
  const actions = [];

  const organization = readAssociation(
    videoDetails,
    "associated_organization",
    "organization_id",
    "organization_slug",
    "organization_name"
  );
  if (organization && organization.slug) {
    const to =
      organization.public_path || organizationPublicPath(organization.slug);
    if (to && to !== "/organizations") {
      actions.push({
        key: "organization",
        label: "View Organization",
        to,
        ariaLabel: organization.name
          ? `View Organization: ${organization.name}`
          : "View Organization",
      });
    }
  }

  const creator = readAssociation(
    videoDetails,
    "associated_creator",
    "creator_id",
    "creator_slug",
    "creator_name"
  );
  if (creator && creator.slug) {
    const to = creator.public_path || creatorPublicPath(creator.slug);
    if (to && to !== "/creators") {
      actions.push({
        key: "creator",
        label: "View Creator",
        to,
        ariaLabel: creator.name
          ? `View Creator: ${creator.name}`
          : "View Creator",
      });
    }
  }

  // Future destinations (Series / Collection / Event): push candidates here.
  // Order is preserved when Video Details maps the full list.

  return actions;
}

/**
 * Single primary contextual CTA (first resolvable destination), or null.
 * Prefer `resolveVideoContextualActions` when rendering the action row.
 *
 * @param {object} videoDetails
 * @returns {{ key: string, label: string, to: string, ariaLabel?: string }|null}
 */
export function resolvePrimaryVideoContextualAction(videoDetails) {
  const actions = resolveVideoContextualActions(videoDetails);
  return actions.length ? actions[0] : null;
}
